<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { api, ServerError } from "@/api/server";
import type { AppUsageEntry, BudgetStatus } from "@/types/protocol";
import { byCategoryOrder, categoryColor } from "@/utils/categories";
import { formatDuration } from "@/utils/format";

/** Wie der Verlauf keine Live-Größe: beim Laden holen, danach minütlich. */
const REFRESH_MS = 60_000;

const entries = ref<AppUsageEntry[]>([]);
const error = ref("");
let timer: number | undefined;

/** Farben kommen aus der gemeinsamen Palette - „Spiele" ist im Verlauf dieselbe Farbe. */
const colorOf = categoryColor;

/** Wie voll ist das Budget? Ueber 100 % wird abgeschnitten - den Rest sagt die Farbe. */
const fill = (budget: BudgetStatus) =>
  `${Math.min(100, (budget.usedSeconds / Math.max(60, budget.limitMinutes * 60)) * 100)}%`;

const minutes = (seconds: number) => Math.floor(seconds / 60);

const hint = (budget: BudgetStatus) =>
  budget.state === "over" ? "aufgebraucht" : `noch ${Math.ceil(budget.remainingSeconds / 60)} min`;

const totals = computed(() =>
  Object.fromEntries(
    entries.value.map((entry) => [
      entry.user,
      entry.categories.reduce((sum, category) => sum + category.seconds, 0),
    ]),
  ),
);

async function load(): Promise<void> {
  try {
    entries.value = await api.appsToday();
    error.value = "";
  } catch (cause) {
    error.value = cause instanceof ServerError ? cause.message : String(cause);
  }
}

onMounted(() => {
  void load();
  timer = window.setInterval(() => void load(), REFRESH_MS);
});

onBeforeUnmount(() => window.clearInterval(timer));
</script>

<template>
  <section>
    <h2>Anwendungen heute</h2>

    <p v-if="error" class="empty">{{ error }}</p>

    <div v-else-if="entries.length" class="rows">
      <article v-for="entry in entries" :key="entry.user" class="panel row">
        <div class="top">
          <span class="who">{{ entry.user }}</span>

          <!-- Vordergrund und Ton getrennt: sonst sieht man der Zeile nicht an,
               ob die Anwendung bedient wird oder nur im Hintergrund spielt. -->
          <span class="live">
            <span v-if="entry.foreground" class="line" :class="{ dim: entry.countsAudio }">
              vorn: <b>{{ entry.foreground.display }}</b>
              <span class="tag" :style="{ borderColor: colorOf(entry.foreground.category) }">
                {{ entry.foreground.category }}
              </span>
              <span v-if="entry.foreground.fullscreen" class="tag full">Vollbild</span>
              <span v-if="!entry.countsAudio" class="tag counts">zählt</span>
            </span>

            <span v-if="entry.audio" class="line" :class="{ dim: !entry.countsAudio }">
              ▶ Ton: <b>{{ entry.audio.display }}</b>
              <span class="tag" :style="{ borderColor: colorOf(entry.audio.category) }">
                {{ entry.audio.category }}
              </span>
              <span v-if="entry.countsAudio" class="tag counts">zählt</span>
            </span>
          </span>
        </div>

        <!-- Feste Reihenfolge wie im Verlauf: nur so lassen sich zwei Tage vergleichen. -->
        <div class="bar" :title="entry.categories.map((c) => `${c.category}: ${formatDuration(c.seconds)}`).join(' · ')">
          <span
            v-for="category in byCategoryOrder(entry.categories)"
            :key="category.category"
            class="slice"
            :style="{
              width: `${(category.seconds / Math.max(1, totals[entry.user])) * 100}%`,
              background: colorOf(category.category),
            }"
          />
        </div>

        <div class="legend">
          <span v-for="category in byCategoryOrder(entry.categories)" :key="category.category">
            <i :style="{ background: colorOf(category.category) }" />
            {{ category.category }} {{ formatDuration(category.seconds) }}
          </span>
        </div>

        <!-- Tagesbudgets: nur Kategorien, fuer die eines konfiguriert ist. -->
        <div v-if="entry.budgets.length" class="budgets">
          <div v-for="budget in entry.budgets" :key="budget.category" class="budget" :class="budget.state">
            <span class="name">{{ budget.category }}</span>
            <span class="track"><i :style="{ width: fill(budget) }" /></span>
            <span class="value">
              {{ minutes(budget.usedSeconds) }}/{{ budget.limitMinutes }} min
              <span v-if="budget.state !== 'ok'" class="hint">· {{ hint(budget) }}</span>
            </span>
          </div>
        </div>

        <table>
          <tbody>
            <tr v-for="app in entry.apps" :key="app.app">
              <td class="app">{{ app.display }}</td>
              <td class="cat">{{ app.category }}</td>
              <td class="time">{{ formatDuration(app.seconds) }}</td>
            </tr>
          </tbody>
        </table>
      </article>
    </div>

    <div v-else class="empty">Noch keine Anwendungsdaten — dafür braucht es Clients ab v1.3.0.</div>
  </section>
</template>

<style scoped>
.rows {
  display: grid;
  gap: 10px;
}

.row {
  padding: 12px 14px;
}

.top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.who {
  font-weight: 600;
}

.live {
  display: grid;
  gap: 2px;
  justify-items: end;
  color: var(--muted);
  font-size: 13px;
}

.live b {
  color: var(--fg);
  font-weight: 500;
}

/* Die Zeile, die gerade nicht gezaehlt wird, tritt zurueck. */
.line.dim {
  opacity: 0.55;
}

.tag.counts {
  border-color: var(--ok);
  color: var(--ok);
}

.tag {
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 1px 8px;
  margin-left: 6px;
  font-size: 12px;
}

.tag.full {
  border-color: var(--warn);
  color: var(--warn);
}

.bar {
  display: flex;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--panel2);
  margin: 10px 0 6px;
}

.slice {
  height: 100%;
}

/* 2px Fläche zwischen zwei Abschnitten - als durchsichtiger Rand innerhalb der
   Breite, damit der Balken nicht wächst (wie im Verlauf). */
.slice:not(:first-child) {
  border-left: 2px solid transparent;
  background-clip: padding-box;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: var(--muted);
  font-size: 12.5px;
}

.legend i {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 2px;
  margin-right: 4px;
}

.budgets {
  display: grid;
  gap: 5px;
  margin-top: 10px;
}

.budget {
  display: grid;
  grid-template-columns: 96px 1fr auto;
  align-items: center;
  gap: 8px;
  color: var(--muted);
  font-size: 12.5px;
}

.budget .track {
  height: 6px;
  border-radius: 999px;
  background: var(--panel2);
  overflow: hidden;
}

.budget .track i {
  display: block;
  height: 100%;
  background: var(--accent);
}

.budget .value {
  font-variant-numeric: tabular-nums;
}

/* Knapp und ueberzogen faerben Balken und Zahl - das ist der Blickfang. */
.budget.warn .track i {
  background: var(--warn);
}

.budget.warn .value {
  color: var(--warn);
}

.budget.over .track i {
  background: var(--danger);
}

.budget.over .value {
  color: var(--danger);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 8px;
}

td {
  padding: 4px 0;
  border-top: 1px solid var(--line);
}

.cat {
  color: var(--muted);
  width: 90px;
}

.time {
  text-align: right;
  font-variant-numeric: tabular-nums;
  width: 90px;
}
</style>
